/**
 * Reset_Password_Screen.tsx — SK Platform
 * Token (from the emailed link, captured in App.tsx) + new password →
 * resetPassword() service. The endpoint is resolved only by Api_Endpoints.ts.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';

import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { clearAuthDeepLink } from '../../navigation/linking';

import PasswordInput from '../../components/passwordInput';
import PrimaryButton from '../../components/primaryButton';
import { useTheme } from '../../context/themeContext';

import { resetPassword, claimResetToken } from '../../services/auth/auth';
import { notifyResetOpened } from '../../navigation/authChannel';
import { isPasswordStrong } from '../../utils/passwordRules';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import AuthBrandHeader from '../../components/authBrandHeader';
import AuthSplitLayout, { useAuthPanelVisible } from '../../components/authSplitLayout';

const Reset_Password_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  // The brand panel carries SOFTKNACK on wide screens; the card shows it only on a phone.
  const showCardBrand = !useAuthPanelVisible();
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'resetPassword'>>();
  const { params } = useRoute<RouteProp<AuthStackParamList, 'resetPassword'>>();
  const token = params?.token ?? '';
  // Finishing the flow: drop the ?mode/?token query so a reload can't re-open
  // it, then return to login.
  const onDone = () => { clearAuthDeepLink(); navigation.navigate('login'); };
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  // Flipped on the first submit press so required-field errors surface even for
  // inputs the user never focused — the button is now always clickable.
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  // Single-open link: on load we CLAIM the emailed token — the server consumes
  // it and returns a fresh in-memory `grant` we submit with. So the link works
  // exactly once; re-opening it claims nothing → the "invalid link" state.
  const [expired, setExpired] = useState(false);   // link dead / already opened
  const [checking, setChecking] = useState(token.length > 0); // claim in flight on load
  const [claimError, setClaimError] = useState(false); // transient network failure
  const [grant, setGrant] = useState<string | null>(null); // credential the form submits with

  const hasToken = token.length > 0;
  // Guards the claim from double-firing (React 18 StrictMode invokes mount
  // effects twice in dev; claiming twice would consume the fresh grant too).
  const claimedRef = useRef(false);

  // Landed here from the emailed link → tell the original "reset link sent"
  // tab to advance itself to this screen (same cross-tab pattern as verify).
  useEffect(() => {
    if (token) notifyResetOpened(token);
  }, [token]);

  // Claim the link. Consumes the emailed token (single-open) and swaps it for a
  // grant. null → dead/already-opened link → "invalid link". A thrown error is
  // a transient network failure → offer a retry rather than a false "invalid".
  const runClaim = useCallback(async () => {
    if (!token) return;
    setChecking(true);
    setClaimError(false);
    try {
      const g = await claimResetToken(token);
      if (g) setGrant(g);
      else setExpired(true);
    } catch {
      setClaimError(true);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token || claimedRef.current) return;
    claimedRef.current = true;
    void runClaim();
  }, [token, runClaim]);

  const passwordValid = isPasswordStrong(password);
  // Field-level validation: empty → "required", then strength / match. Shown
  // once the field is touched (blur) or a submit was attempted.
  const passwordError = !password
    ? 'Password is required'
    : !passwordValid
      ? 'Password does not meet the requirements below'
      : undefined;
  const confirmError = !confirmPassword
    ? 'Confirm password is required'
    : confirmPassword !== password
      ? 'Passwords do not match'
      : undefined;

  const handleSubmit = async () => {
    // The button is always clickable; a press first reveals any validation
    // errors, and only a fully-valid form actually hits the API.
    if (isLoading || done) return;
    setSubmitted(true);
    setConfirmTouched(true);
    if (!grant || passwordError || confirmError) return;

    setIsLoading(true);
    try {
      const res = await resetPassword(grant, password);
      setDone(true);
      showSuccess(res, 'Password reset', res.message);
      onDone?.();
    } catch (err) {
      // 401 → the reset token was already used or has expired (single-use).
      // Surface the dedicated expired state; anything else is a generic toast.
      if ((err as any)?.response?.status === 401) {
        setExpired(true);
      } else {
        handleApiError(err, 'Reset failed', 'Reset failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requestNewLink = () => {
    clearAuthDeepLink();
    navigation.navigate('forgotPassword');
  };

  return (
    <AuthSplitLayout
      headline="Almost there, set your new password"
      sub="Thank you for keeping your account safe. Choose a new password, and you can sign in again right away."
      points={[
        'At least 8 characters long',
        'Mix upper and lower case letters, a number and a symbol',
        'Use a password you do not use anywhere else',
      ]}
    >
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
            {showCardBrand ? <AuthBrandHeader /> : null}

            <Text style={[styles.title, { color: colors.ink }]}>Set a new password</Text>
            <Text style={[styles.sub, { color: colors.ink2 }]}>Choose a strong password you don't use elsewhere.</Text>

            {checking ? (
              <View style={styles.form}>
                <View style={{ alignItems: 'center', paddingVertical: 20 }}>
                  <ActivityIndicator size="large" color={colors.accent} />
                  <Text style={[styles.sub, { color: colors.ink2, marginTop: 14, marginBottom: 0 }]}>
                    Verifying your reset link…
                  </Text>
                </View>
              </View>
            ) : claimError ? (
              <View style={styles.form}>
                <View style={[styles.errorBox, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}>
                  <Text style={[styles.errorText, { color: colors.danger }]}>
                    We couldn't verify your reset link just now. Please check your connection
                    and try again.
                  </Text>
                </View>
                <PrimaryButton title="Try again" onPress={() => runClaim()} style={styles.submitBtn} />
                <View style={styles.footer}>
                  <Text style={[styles.footerText, { color: colors.ink3 }]}>Still stuck? </Text>
                  <PrimaryButton
                    title="Request a new link  →"
                    onPress={requestNewLink}
                    variant="ghost"
                    size="small"
                    style={styles.footerLinkBtn}
                    textStyle={[styles.footerLinkText, { color: colors.accent }]}
                  />
                </View>
              </View>
            ) : expired ? (
              <View style={styles.form}>
                <View style={[styles.errorBox, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}>
                  <Text style={[styles.errorText, { color: colors.danger }]}>
                    This reset link has expired or was already used. Reset links work only
                    once — request a fresh one to continue.
                  </Text>
                </View>
                <PrimaryButton title="Request a new link" onPress={requestNewLink} style={styles.submitBtn} />
                <View style={styles.footer}>
                  <Text style={[styles.footerText, { color: colors.ink3 }]}>Changed your mind? </Text>
                  <PrimaryButton
                    title="Back to login  →"
                    onPress={() => onDone?.()}
                    variant="ghost"
                    size="small"
                    style={styles.footerLinkBtn}
                    textStyle={[styles.footerLinkText, { color: colors.accent }]}
                  />
                </View>
              </View>
            ) : !hasToken ? (
              <View style={styles.form}>
                <View style={[styles.errorBox, { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine }]}>
                  <Text style={[styles.errorText, { color: colors.danger }]}>
                    This reset link is missing or invalid. Request a new link from “Forgot
                    password”.
                  </Text>
                </View>
                <PrimaryButton title="Back to login" onPress={() => onDone?.()} style={styles.submitBtn} />
              </View>
            ) : (
              <View style={styles.form}>
                <PasswordInput
                  label="New password"
                  required
                  placeholder="Enter new password"
                  value={password}
                  onChangeText={setPassword}
                  touched={submitted}
                  error={passwordError}
                  showRules={true}
                  mode="signup"
                />

                <PasswordInput
                  label="Confirm password"
                  required
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  touched={submitted || confirmTouched}
                  error={confirmError}
                  mode="login"
                  onBlur={() => setConfirmTouched(true)}
                  onSubmitEditing={handleSubmit}
                />

                <View style={styles.actionWrap}>
                  <PrimaryButton
                    title={done ? 'Password reset  ✓' : isLoading ? 'Resetting…' : 'Reset password'}
                    onPress={handleSubmit}
                    disabled={isLoading || done}
                    loading={isLoading}
                    hoverLift
                    style={styles.submitBtn}
                    textStyle={styles.submitBtnText}
                  />
                </View>

                <View style={styles.footer}>
                  <Text style={[styles.footerText, { color: colors.ink3 }]}>Remembered it? </Text>
                  <PrimaryButton
                    title="Back to login  →"
                    onPress={() => onDone?.()}
                    variant="ghost"
                    size="small"
                    style={styles.footerLinkBtn}
                    textStyle={[styles.footerLinkText, { color: colors.accent }]}
                  />
                </View>
              </View>
            )}
          </View>
    </AuthSplitLayout>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow:
            '0 24px 60px -18px rgba(15,23,42,0.22), 0 6px 18px -8px rgba(15,23,42,0.10)',
        } as any)
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 14 },
          shadowOpacity: 0.14,
          shadowRadius: 32,
          elevation: 10,
        }),
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -1,
    lineHeight: 38,
  },
  sub: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 28,
    textAlign: 'center',
    lineHeight: 22,
  },
  form: { width: '100%' },
  errorBox: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  errorText: { fontSize: 13, lineHeight: 20, fontWeight: '500' },
  actionWrap: { marginTop: 18 },
  submitBtn: {
    height: 52,
    minHeight: 52,
    borderRadius: 14,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 14px 28px rgba(11,11,13,0.32)' } as any)
      : { shadowColor: '#0B0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.32, shadowRadius: 22, elevation: 10 }),
  },
  submitBtnText: {
    fontSize: 15,
    letterSpacing: 1.2,
  },
  footer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 16 },
  footerText: { fontSize: 14, fontWeight: '500' },
  footerLinkBtn: { width: 'auto' as any, minHeight: 'auto' as any, paddingHorizontal: 0 },
  footerLinkText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 14,
    letterSpacing: 0,
  },
});

export default Reset_Password_Screen;
